import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { useSupabase } from '@/hooks/useSupabase';
import { supabase } from '@/lib/supabase';
import type { ProspectStatus } from '@/types/database.types';

import {
  createProspect,
  getProspect,
  listProspectOptions,
  listProspectShares,
  listProspects,
  listRecentProspects,
  patchProspectFields,
  revokeShare,
  shareProspect,
  softDeleteProspect,
  updateProspect,
  updateProspectStatus,
  type ProspectListFilters,
  type ProspectListResult,
  type ProspectOption,
  reassignProspectOwner,
} from './api';
import type { ProspectFormValues, ShareProspectValues } from './schema';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const prospectsKeys = {
  all: ['prospects'] as const,
  counts: () => [...prospectsKeys.all, 'counts'] as const,
  lists: () => [...prospectsKeys.all, 'list'] as const,
  list: (filters: ProspectListFilters, page: number) =>
    [...prospectsKeys.lists(), filters, page] as const,
  details: () => [...prospectsKeys.all, 'detail'] as const,
  detail: (id: string) => [...prospectsKeys.details(), id] as const,
  shares: (id: string) => [...prospectsKeys.detail(id), 'shares'] as const,
  recent: () => [...prospectsKeys.all, 'recent'] as const,
};

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export function useProspects(filters: ProspectListFilters, page: number) {
  return useQuery({
    queryKey: prospectsKeys.list(filters, page),
    queryFn: ({ signal }) => listProspects(filters, page, signal),
    placeholderData: keepPreviousData,
  });
}

/**
 * Últimos prospectos abiertos por el usuario (tab Recientes). `enabled` para
 * no disparar la query salvo cuando el tab está activo.
 */
/**
 * Prospectos vistos recientemente (tope 10, ver RECENT_LIMIT en api.ts).
 *
 * Corre SIEMPRE, no solo cuando la pestaña "Recientes" está activa: su
 * contador vive en la barra de pestañas del listado, así que con la query
 * apagada el número arrancaba en 0 y recién se corregía al entrar a la
 * pestaña — o sea, mostraba 0 justo cuando el dato importaba. Son 10 filas
 * como mucho; el ahorro de no pedirlas no compensa un contador que miente.
 */
export function useRecentProspects() {
  return useQuery({
    queryKey: prospectsKeys.recent(),
    queryFn: listRecentProspects,
    staleTime: 10_000,
  });
}

/**
 * Opciones livianas (id + name) de TODOS los prospects activos, para
 * comboboxes de vinculación. Sin paginar — ver `listProspectOptions`.
 */
export function useProspectOptions() {
  return useQuery<ProspectOption[]>({
    queryKey: [...prospectsKeys.all, 'options'] as const,
    queryFn: listProspectOptions,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Status counts (para tabs del listado)
// ---------------------------------------------------------------------------

export interface ProspectsStatusCounts {
  total: number;
  newThisWeek: number;
  byStatus: Record<ProspectStatus, number>;
}

/**
 * Trae status + created_at de todos los prospects activos y agrupa
 * client-side. Para volúmenes < 5k filas es más simple y barato que
 * un RPC. Si crece, mover a una vista materializada o un RPC.
 */
async function fetchProspectsStatusCounts(): Promise<ProspectsStatusCounts> {
  const { data, error } = await supabase
    .from('prospects')
    .select('status, created_at')
    .eq('deleted', false);
  if (error) throw error;

  const byStatus = {
    new: 0,
    researching: 0,
    qualified: 0,
    contacted: 0,
    in_conversation: 0,
    negotiating: 0,
    won: 0,
    lost: 0,
    archived: 0,
  } as Record<ProspectStatus, number>;

  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  let newThisWeek = 0;
  for (const row of data ?? []) {
    byStatus[row.status as ProspectStatus] =
      (byStatus[row.status as ProspectStatus] ?? 0) + 1;
    if (row.created_at && new Date(row.created_at).getTime() >= weekAgo) {
      newThisWeek += 1;
    }
  }
  return { total: data?.length ?? 0, newThisWeek, byStatus };
}

export function useProspectsStatusCounts() {
  return useQuery({
    queryKey: prospectsKeys.counts(),
    queryFn: fetchProspectsStatusCounts,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

export function useProspect(id: string | undefined) {
  return useQuery({
    queryKey: id ? prospectsKeys.detail(id) : ['prospects', 'detail', 'unset'],
    queryFn: () => getProspect(id!),
    enabled: !!id,
    // «Sin filas» (PGRST116) es una ficha ajena o inexistente, no un fallo
    // transitorio: reintentar solo demora la pantalla de pedir acceso.
    retry: (n, err) => (err as { code?: string } | null)?.code !== 'PGRST116' && n < 1,
  });
}

/**
 * Helper para invalidar el detail de un prospect desde mutaciones externas
 * (ej. `core-prospect-ai-resolution` updatea columnas vía edge function).
 */
export function useProspectQueryInvalidator() {
  const queryClient = useQueryClient();
  return (id: string) => {
    queryClient.invalidateQueries({ queryKey: prospectsKeys.detail(id) });
  };
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export function useCreateProspect() {
  const queryClient = useQueryClient();
  const { user } = useSupabase();

  return useMutation({
    mutationFn: async (values: ProspectFormValues) => {
      if (!user) throw new Error('Sin sesión');
      return createProspect(values, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prospectsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: prospectsKeys.counts() });
    },
  });
}

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

export function useUpdateProspect(id: string) {
  const queryClient = useQueryClient();
  const { user } = useSupabase();

  return useMutation({
    mutationFn: async (values: ProspectFormValues) => {
      if (!user) throw new Error('Sin sesión');
      return updateProspect(id, values, user.id);
    },
    onSuccess: (row) => {
      queryClient.setQueryData(prospectsKeys.detail(id), row);
      queryClient.invalidateQueries({ queryKey: prospectsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: prospectsKeys.counts() });
    },
  });
}

/**
 * Mutation parcial de campos sueltos del prospecto (texto de IA: pain_point /
 * opportunity / business_context, y las 3 estimaciones numéricas de la
 * propuesta editables inline). Usado al aceptar una propuesta de IA y por la
 * edición inline del detalle. Refresca el detalle (`setQueryData`).
 */
export function useApplyProspectFields(id: string) {
  const queryClient = useQueryClient();
  const { user } = useSupabase();
  return useMutation({
    mutationFn: async (patch: {
      pain_point?: string;
      opportunity?: string;
      business_context?: string;
      internal_notes?: string;
      proposed_solution?: string;
      recommended_approach?: string;
      estimated_deal_usd?: number | null;
      estimated_cycle_days?: number | null;
      close_probability?: number | null;
      // Datos de empresa (modal de edición del detalle).
      legal_name?: string | null;
      tax_id?: string | null;
      sector?: string | null;
      scale?: string | null;
      website?: string | null;
      address?: string | null;
      city?: string | null;
      province?: string | null;
      country_code?: string | null;
      // Datos fiscales para facturación ARCA (FACT-01 · Factura C).
      iva_condition_id?: number | null;
      fiscal_doc_tipo?: number | null;
      // Datos fiscales para facturar al exterior (factura E).
      fex_dst_pais?: number | null;
      fex_cuit_pais?: number | null;
    }) => {
      if (!user) throw new Error('Sin sesión');
      return patchProspectFields(id, patch, user.id);
    },
    onSuccess: (row) => {
      queryClient.setQueryData(prospectsKeys.detail(id), row);
      queryClient.invalidateQueries({ queryKey: prospectsKeys.lists() });
    },
  });
}

/**
 * Mutation parcial · solo cambia status. Usado por Kanban drag&drop con
 * optimistic update real — la card se mueve al instante en todas las queries
 * de lista cacheadas y se hace rollback si el round-trip falla.
 */
export function useReassignProspectOwner() {
  const queryClient = useQueryClient();
  const { user } = useSupabase();

  return useMutation({
    mutationFn: async (args: { id: string; ownerUserId: string }) => {
      if (!user) throw new Error('Sin sesión');
      return reassignProspectOwner(args.id, args.ownerUserId, user.id);
    },
    onSettled: (_data, _err, args) => {
      queryClient.invalidateQueries({ queryKey: prospectsKeys.detail(args.id) });
      queryClient.invalidateQueries({ queryKey: prospectsKeys.lists() });
    },
  });
}

export function useUpdateProspectStatus() {
  const queryClient = useQueryClient();
  const { user } = useSupabase();

  return useMutation({
    mutationFn: async (args: { id: string; status: ProspectStatus }) => {
      if (!user) throw new Error('Sin sesión');
      return updateProspectStatus(args.id, args.status, user.id);
    },
    onMutate: async (args) => {
      // 1. Cancelar refetches en vuelo para que no pisen el update optimista.
      await queryClient.cancelQueries({ queryKey: prospectsKeys.lists() });

      // 2. Snapshot de todas las queries de lista cacheadas (varios
      //    filters/page) para poder revertir.
      const previous = queryClient.getQueriesData<ProspectListResult>({
        queryKey: prospectsKeys.lists(),
      });

      // 3. Aplicar el nuevo status en cada cache de lista que contenga la card.
      for (const [key, data] of previous) {
        if (!data) continue;
        queryClient.setQueryData<ProspectListResult>(key, {
          ...data,
          rows: data.rows.map((row) =>
            row.id === args.id ? { ...row, status: args.status } : row,
          ),
        });
      }

      return { previous };
    },
    onError: (_err, _args, context) => {
      // Rollback: restaurar cada snapshot exactamente como estaba.
      if (!context?.previous) return;
      for (const [key, data] of context.previous) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: (_data, _err, args) => {
      // Reconciliar con el server (recuento por status incluido).
      queryClient.invalidateQueries({ queryKey: prospectsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: prospectsKeys.counts() });
      queryClient.invalidateQueries({ queryKey: prospectsKeys.detail(args.id) });
    },
  });
}

// ---------------------------------------------------------------------------
// Delete (soft)
// ---------------------------------------------------------------------------

export function useDeleteProspect() {
  const queryClient = useQueryClient();
  const { user } = useSupabase();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!user) throw new Error('Sin sesión');
      await softDeleteProspect(id, user.id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prospectsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: prospectsKeys.counts() });
    },
  });
}

// ---------------------------------------------------------------------------
// Shares
// ---------------------------------------------------------------------------

export function useProspectShares(id: string | undefined) {
  return useQuery({
    queryKey: id ? prospectsKeys.shares(id) : ['prospect_shares', 'unset'],
    queryFn: () => listProspectShares(id!),
    enabled: !!id,
  });
}

export function useShareProspect(prospectId: string) {
  const queryClient = useQueryClient();
  const { user } = useSupabase();

  return useMutation({
    mutationFn: async (values: ShareProspectValues) => {
      if (!user) throw new Error('Sin sesión');
      return shareProspect(prospectId, values, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: prospectsKeys.shares(prospectId),
      });
    },
  });
}

export function useRevokeShare(prospectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sharedWithUserId: string) => {
      await revokeShare(prospectId, sharedWithUserId);
      return sharedWithUserId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: prospectsKeys.shares(prospectId),
      });
    },
  });
}
